import { createContext, useState, useRef, useEffect } from 'react'
import _groupBy from 'lodash/groupBy'
import { GET_SLOTS } from 'dataSource/ProviderSearch/routes'
import useApi from 'hooks/useApi'
import { getParsedQuery, getTimeZoneFormattedDate } from 'utils/common'
import { dateFormats } from 'constants/common'
import { sortGeneratedSlots } from 'utils/common'
import { CachePolicies } from 'use-http'
import { getSlotsStartEndDate } from 'app/ProviderSearch/utils'
import { useRouter } from 'next/router'
import {
  RETURN_FILTER_KEY_MAPPING,
  LabelAppointmentMapping,
} from 'app/ProviderSearch/constants'

const initialValue: ProviderScheduleDetails = {
  providerSlots: {},
  loading: true,
  error: null,
  nextAvailabilityForProviders: null,
}

type GetProviderSlotsParams = {
  startSlotDate: Date
  timeZone: string
  providerList: Provider[]
  practiceId?: string
  slotListLimit: number
  availabilityFilters?: ProviderFilter[]
  slotsType?: string
  allowSlotsAPICall?: boolean
  skipTimeOfDayFilter?: boolean
}

export const getProviderSlots = ({
  startSlotDate,
  providerList,
  practiceId,
  timeZone,
  slotListLimit,
  availabilityFilters = null,
  slotsType = '',
  allowSlotsAPICall = true,
  skipTimeOfDayFilter = false,
}: GetProviderSlotsParams): ProviderScheduleDetails => {
  const lastRequestRef = useRef<string>(null)
  const router = useRouter()
  const [
    providerSlots,
    setProviderSlots,
  ] = useState<NormalizedProviderSchedule>(initialValue.providerSlots)
  const [
    nextAvailabilityForProviders,
    setNextAvailability,
  ] = useState<NextAvailabilityMap>(null)

  /**
   * Using a state instead of request.loading because noticed on dev
   * There seems to be a delay to initiate the api call so a pause can be noticed in between
   * the slots api call being made and its loader showing up
   * this is not noticed on qa
   */
  const [loading, setLoading] = useState<boolean>(initialValue.loading)
  const { error, request } = useApi(null, null, CachePolicies.NETWORK_ONLY)

  const fetchProviderSlots = async (requestRef: string) => {
    setLoading(true)
    const generatedProviderSlots = {}
    const nextAvailabilityMap: NextAvailabilityMap = {}
    const { startDate, endDate } = getSlotsStartEndDate(
      startSlotDate,
      slotListLimit,
      timeZone
    )
    const formattedQuery = getParsedQuery(router) as Record<
      string,
      string | string[]
    >
    let type = slotsType

    const availabilityTypeFromQuery =
      formattedQuery[RETURN_FILTER_KEY_MAPPING.availability_type]

    if (availabilityTypeFromQuery?.length > 0 && availabilityFilters) {
      // Fetch the label for the availabilityType id in query
      const availabilityLabelFromId = availabilityFilters.find(
        (filter) => filter.id === availabilityTypeFromQuery?.[0]
      ).value

      // Fetch the enum value for the same availability label
      const availabilityValueFromLabel =
        LabelAppointmentMapping[availabilityLabelFromId]

      type = availabilityValueFromLabel
    }

    // use-http automatically converst date object to utc time
    const slots = await request.post(GET_SLOTS, {
      start: startDate,
      end: endDate,
      for: providerList.map((provider) => ({
        providerId: provider.id,
        practiceId: practiceId ?? provider?.practice?.id,
      })),
      ...(formattedQuery.timeOfDay &&
        !skipTimeOfDayFilter && {
          timeOfDay: formattedQuery.timeOfDay,
          timezone: timeZone,
        }),
      ...(!!type && { type }),
    })

    if (lastRequestRef.current !== requestRef) {
      return
    }

    const groupedSlots = _groupBy(slots, (slot) => slot.providerId)
    providerList?.map((provider) => {
      const providerSlots = groupedSlots[provider.id]

      if (providerSlots?.length) {
        const groupedDateSlots: DateSlot = _groupBy(
          providerSlots[0]?.slots,
          (slot) =>
            getTimeZoneFormattedDate(
              slot.start,
              dateFormats.YEAR_MONTH_DAY_FORMAT,
              timeZone
            )
        )
        const sortedGroupedDateSlots = sortGeneratedSlots(groupedDateSlots)
        generatedProviderSlots[provider.id] = sortedGroupedDateSlots
        //creating next availability map for providers
        nextAvailabilityMap[provider.id] =
          providerSlots?.[0]?.nextAvailableSlot ?? null
      }
    })
    setNextAvailability(nextAvailabilityMap)
    setProviderSlots(generatedProviderSlots)
    setLoading(false)
  }

  useEffect(() => {
    // Whenever data list changes it would update the slots
    if (startSlotDate && providerList?.length && allowSlotsAPICall) {
      const newRequestTimestampRef = new Date().getTime().toString()
      lastRequestRef.current = newRequestTimestampRef
      fetchProviderSlots(newRequestTimestampRef)
    }
  }, [startSlotDate, providerList, slotListLimit, allowSlotsAPICall])

  return {
    providerSlots,
    nextAvailabilityForProviders,
    loading,
    error,
  }
}

const ProviderSlotContext = createContext(initialValue)
export default ProviderSlotContext
